/**
 * ── A MINIMAL ZIP WRITER (STORE method, no compression) ─────────────────────────────
 *
 * Written for the analytics export (VERA-260), which delivers one CSV per card on the
 * canvas and therefore needs a container.
 *
 * ## Why this is ~120 lines of hand-rolled format instead of a dependency
 *
 * §28 and §47 both say reach for the existing thing first, so this needs a reason.
 * `fflate@0.8.3` and `jszip@3.10.1` are both present in the pnpm store as TRANSITIVE
 * dependencies of something else, and neither resolves from `apps/platform`:
 *
 *     $ cd apps/platform && node -e "require.resolve('fflate')"   -> MODULE_NOT_FOUND
 *     $ cd apps/platform && node -e "require.resolve('jszip')"    -> MODULE_NOT_FOUND
 *
 * Importing either would be a PHANTOM DEPENDENCY — it resolves today only if some other
 * package's version happens to be hoisted, and it breaks on an unrelated dependency bump
 * with an error that points nowhere near the cause. Adding one as a real dependency means
 * a lockfile change and a `pnpm install` across ~30 live worktrees for a format whose
 * writer, in the STORE case, is the 120 lines below.
 *
 * ## STORE, not DEFLATE, and that is the whole reason this is small
 *
 * Compression is what makes a zip writer big — DEFLATE needs a full LZ77 + Huffman
 * encoder. The ZIP specification has always allowed method 0 (stored, byte-for-byte), and
 * every extractor on every platform reads it: macOS Archive Utility, Windows Explorer's
 * built-in folder view, `unzip`, Python's `zipfile`, 7-Zip.
 *
 * The cost is file size, and for this payload it is not a cost worth paying to avoid: an
 * analytics export is a handful of CSVs, the largest of which (a scan-map point list) is
 * a few hundred KB at the ceiling the endpoint returns. It is generated in the browser and
 * saved to the user's own disk — it never crosses a network. Trading a ~4x size reduction
 * for a phantom dependency or an encoder to maintain is the wrong side of that trade.
 *
 * ⚠️ IF THIS EVER NEEDS TO CARRY IMAGES OR A LARGE EXPORT, add a real dependency rather
 * than growing a DEFLATE implementation here. The line is: this file stays this size.
 *
 * ## What is deliberately NOT implemented
 *
 *   - ZIP64. The 4 GB / 65535-entry limits are unreachable for this payload, and a
 *     writer that silently emitted a corrupt archive past them would be worse than one
 *     that cannot reach them at all. `buildZip` throws instead (see below).
 *   - Directory entries. Nested paths work by putting `/` in a filename, which is what
 *     every writer does; explicit directory records are optional in the format.
 *   - Data descriptors. Sizes and CRCs are known before writing because the whole
 *     archive is assembled in memory.
 *   - Encryption, comments, extra fields.
 */

/**
 * CRC-32 (IEEE 802.3 polynomial, reflected — `0xEDB88320`).
 *
 * The table is built once on first use rather than written out as a 256-entry literal:
 * the generator loop is auditable in a way a wall of hex constants is not, and a typo in
 * a literal would produce an archive that every extractor rejects with "CRC failed" and
 * no indication of which byte is wrong.
 */
let crcTable: Uint32Array | null = null

const getCrcTable = (): Uint32Array => {
  if (crcTable) return crcTable
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[i] = c >>> 0
  }
  crcTable = table
  return table
}

const crc32 = (bytes: Uint8Array): number => {
  const table = getCrcTable()
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) {
    c = table[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  }
  return (c ^ 0xffffffff) >>> 0
}

/**
 * A date/time in MS-DOS format — the only timestamp ZIP's base record carries.
 *
 * Two-second resolution and a 1980 epoch, both properties of the format rather than of
 * this implementation. LOCAL time, not UTC: the field has no timezone, and every writer
 * treats it as local, so using UTC here would make a file's stated mtime disagree with
 * every other archive on the user's disk by their offset.
 */
const dosDateTime = (d: Date): { date: number; time: number } => {
  const year = Math.max(1980, d.getFullYear())
  return {
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1),
  }
}

export interface ZipEntry {
  /** Path within the archive. `/` nests; no leading slash. */
  name: string
  /** File contents. Text callers encode with `TextEncoder` first. */
  data: Uint8Array
}

/** Entry count and byte ceilings imposed by the non-ZIP64 record layout. */
const MAX_ENTRIES = 0xffff
const MAX_BYTES = 0xffffffff

/**
 * Build a ZIP archive as a Blob.
 *
 * Throws rather than truncating when the payload exceeds what a non-ZIP64 archive can
 * describe. An archive whose central directory disagrees with its contents opens as
 * "empty" or "damaged" in different extractors, so the failure is raised where it can be
 * reported instead of shipped to the user's disk.
 */
export const buildZip = (entries: readonly ZipEntry[], modifiedAt: Date = new Date()): Blob => {
  if (entries.length > MAX_ENTRIES) {
    throw new Error(`Cannot zip ${entries.length} files — the archive format used here holds at most ${MAX_ENTRIES}.`)
  }

  const encoder = new TextEncoder()
  const { date, time } = dosDateTime(modifiedAt)

  /** Local file headers + data, in order. Also the body of the finished archive. */
  const localParts: Uint8Array[] = []
  /** Central directory records, written after every local part. */
  const centralParts: Uint8Array[] = []

  let offset = 0

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name)
    const data = entry.data
    const crc = crc32(data)

    if (offset > MAX_BYTES || data.length > MAX_BYTES) {
      throw new Error('Export is too large for the archive format used here.')
    }

    // ── Local file header (30 bytes + name) ──────────────────────────────────────────
    const local = new Uint8Array(30 + nameBytes.length)
    const lv = new DataView(local.buffer)
    lv.setUint32(0, 0x04034b50, true) // signature
    lv.setUint16(4, 20, true) // version needed: 2.0
    /*
      General purpose flag bit 11 — the filename is UTF-8.

      Set unconditionally rather than only for non-ASCII names, because a card title can
      legitimately contain any character and a filename decoded under a legacy codepage is
      the same mojibake failure the CSV BOM exists to prevent (see `utils/csv.ts`), one
      level up in the archive.
    */
    lv.setUint16(6, 0x0800, true)
    lv.setUint16(8, 0, true) // method 0 — STORE. See the header.
    lv.setUint16(10, time, true)
    lv.setUint16(12, date, true)
    lv.setUint32(14, crc, true)
    lv.setUint32(18, data.length, true) // compressed size == uncompressed under STORE
    lv.setUint32(22, data.length, true)
    lv.setUint16(26, nameBytes.length, true)
    lv.setUint16(28, 0, true) // no extra field
    local.set(nameBytes, 30)

    localParts.push(local, data)

    // ── Central directory record (46 bytes + name) ───────────────────────────────────
    const central = new Uint8Array(46 + nameBytes.length)
    const cv = new DataView(central.buffer)
    cv.setUint32(0, 0x02014b50, true) // signature
    cv.setUint16(4, 20, true) // version made by
    cv.setUint16(6, 20, true) // version needed
    cv.setUint16(8, 0x0800, true) // UTF-8 name, matching the local header
    cv.setUint16(10, 0, true) // STORE
    cv.setUint16(12, time, true)
    cv.setUint16(14, date, true)
    cv.setUint32(16, crc, true)
    cv.setUint32(20, data.length, true)
    cv.setUint32(24, data.length, true)
    cv.setUint16(28, nameBytes.length, true)
    cv.setUint16(30, 0, true) // extra length
    cv.setUint16(32, 0, true) // comment length
    cv.setUint16(34, 0, true) // disk number
    cv.setUint16(36, 0, true) // internal attributes
    cv.setUint32(38, 0, true) // external attributes
    cv.setUint32(42, offset, true) // offset of the local header
    central.set(nameBytes, 46)

    centralParts.push(central)

    offset += local.length + data.length
  }

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0)

  // ── End of central directory (22 bytes, no comment) ────────────────────────────────
  const end = new Uint8Array(22)
  const ev = new DataView(end.buffer)
  ev.setUint32(0, 0x06054b50, true)
  ev.setUint16(4, 0, true) // this disk
  ev.setUint16(6, 0, true) // disk with central directory
  ev.setUint16(8, entries.length, true)
  ev.setUint16(10, entries.length, true)
  ev.setUint32(12, centralSize, true)
  ev.setUint32(16, offset, true) // central directory starts where the local parts end
  ev.setUint16(20, 0, true) // no archive comment

  /*
    `.buffer` rather than the views themselves.

    TypeScript 5.7 made `Uint8Array` generic in its backing buffer (`Uint8Array<ArrayBufferLike>`),
    and the DOM lib's `BlobPart` still names the pre-generic `ArrayBufferView`, so passing a
    view straight in is a type error under this repo's TS 5.9 — three of them on this one
    line. Every array here is allocated at full length and never sub-viewed, so `byteOffset`
    is 0 and `byteLength` is the whole buffer: handing over `.buffer` is byte-for-byte the
    same payload, not a widening that could silently include slack.

    ⚠️ That equivalence is a property of how these are built. If any part ever becomes a
    `subarray`, this must go back to passing the VIEW (with whatever cast the lib versions
    then require), or the archive would gain the bytes around it.
  */
  const parts: BlobPart[] = [...localParts, ...centralParts, end].map(p => p.buffer as ArrayBuffer)
  return new Blob(parts, { type: 'application/zip' })
}
